import { Component, computed, inject, signal } from "@angular/core";
import { TruncateTextPipe } from "@kno2/common/pipes";
import { NotificationService } from "@kno2/common/services/notifications";
import { AuthService } from "@kno2/core/auth";
import { SessionService } from "@kno2/core/session";
import { IdProofingService } from "@kno2/data-access/id-proofing";
import { DialogBodyComponent, DialogFooterComponent, DialogService } from "@kno2/shared/dialog";
import { NotificationAlertComponent } from "@kno2/shared/notification-alert";
import { hasValue } from "@kno2/utils";

@Component({
    selector: "kno2-onboarding-id-proofing-manual-modal",
    imports: [DialogBodyComponent, DialogFooterComponent, NotificationAlertComponent, TruncateTextPipe],
    templateUrl: "./onboarding-id-proofing-manual-modal.component.html"
})
export class OnboardingIdProofingManualModalComponent {
    private readonly dialogService = inject(DialogService);
    private readonly authService = inject(AuthService);
    private readonly idProofingService = inject(IdProofingService);
    private readonly notificationService = inject(NotificationService);
    private readonly sessionService = inject(SessionService);

    protected readonly selectedFile = signal<File | null>(null);
    protected readonly isUploading = signal(false);
    protected readonly error = signal<string | null>(null);
    protected readonly hasInteracted = signal(false);

    protected onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] || null;

        this.selectedFile.set(file);
        this.hasInteracted.set(true);

        if (file) {
            this.error.set(null);
        }
    }

    protected async uploadId(): Promise<void> {
        const file = this.selectedFile();
        if (!file) {
            this.error.set("Please select a file to upload");
            this.hasInteracted.set(true);
            return;
        }

        try {
            this.isUploading.set(true);
            this.error.set(null);

            await this.idProofingService.submitManualAsync(file);

            this.notificationService.success("Identification has been sent.");

            await this.sessionService.refreshProfile();

            this.dialogService.close(true);
        } catch {
            this.error.set("Failed to upload file. Please try again.");
        } finally {
            this.isUploading.set(false);
        }
    }

    protected async closeModal(): Promise<void> {
        await this.authService.logout();
    }

    protected isFormValid = computed(() => hasValue(this.selectedFile()));

    protected fileRequired = computed(() => this.hasInteracted() && !hasValue(this.selectedFile()));
}
